export const SubscriptionState = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
  SUSPENDED: 'SUSPENDED',
} as const;
export type SubscriptionState = (typeof SubscriptionState)[keyof typeof SubscriptionState];

export const AccountingDocumentState = {
  DRAFT: 'DRAFT',
  SENT: 'SENT',
  PAID: 'PAID',
  OVERDUE: 'OVERDUE',
  CANCELLED: 'CANCELLED',
} as const;
export type AccountingDocumentState =
  (typeof AccountingDocumentState)[keyof typeof AccountingDocumentState];

export const AccountingDocumentType = {
  INVOICE: 'INVOICE',
  CREDIT_NOTE: 'CREDIT_NOTE',
  DEBIT_NOTE: 'DEBIT_NOTE',
  RECEIPT: 'RECEIPT',
} as const;
export type AccountingDocumentType =
  (typeof AccountingDocumentType)[keyof typeof AccountingDocumentType];

export const PaymentState = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
} as const;
export type PaymentState = (typeof PaymentState)[keyof typeof PaymentState];

export interface Tenant {
  id: string;
  name: string;
  resellerId?: number | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface Organization {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  status?: string;
  isDefault?: boolean;
  type?: string | null;
  creditBalance?: number;
  createdAt: string;
  updatedAt?: string | null;
  tenant?: { id: string; name: string; resellerId?: number | null } | null;
  units?: OrganizationUnitRef[];
  /** Adresse & Kontakt (optional, wenn vom Backend bereitgestellt) */
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface OrganizationUnitRef {
  id: string;
  name: string;
  description?: string | null;
  sort?: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface Subscription {
  id: string;
  tenantId: string;
  number: string;
  state: SubscriptionState;
  intervalMonths?: number | null;
  amountNet?: number | null;
  amountGross?: number | null;
  currencyCode?: string | null;
  activationDate?: string | null;
  cancellationDate?: string | null;
  expiryDate?: string | null;
  currentTermStartDate?: string | null;
  currentTermEndDate?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  organizationUnit?: { id: string; name: string } | null;
  paymentAccount?: {
    id: string;
    paymentGateway?: { id: string; name: string } | null;
    paymentTerm?: { id: string; name: string } | null;
  } | null;
  subscriptionItems?: SubscriptionItem[];
}

export interface SubscriptionItem {
  id: string;
  subscriptionId?: string;
  planId?: string | null;
  quantity: number;
  unitPriceNet?: number | null;
  unitPriceGross?: number | null;
  plan?: { id: string; name: string } | null;
}

export interface AccountingDocument {
  id: string;
  tenantId: string;
  type: AccountingDocumentType;
  number: string;
  state: AccountingDocumentState;
  currencyCode: string;
  amountNet: number;
  amountGross: number;
  taxAmount: number;
  createdAt: string;
  updatedAt?: string | null;
  sentAt?: string | null;
  documentHeader?: string | null;
  documentFooter?: string | null;
  privateNotice?: string | null;
  publicNotice?: string | null;
  subscription?: { id: string; number: string } | null;
  items?: AccountingDocumentItem[];
  payments?: Array<{
    id: string;
    state: string;
    direction: string;
    amountGross: number;
    paymentDate?: string | null;
    createdAt: string;
    paymentGateway?: { id: string; name: string } | null;
  }>;
}

export interface AccountingDocumentItem {
  id: string;
  accountingDocumentId?: string;
  name: string;
  quantity: number;
  unitPriceNet: number;
  unitPriceGross: number;
  amountNet: number;
  amountGross: number;
}

export interface Payment {
  id: string;
  accountingDocumentId: string;
  state: PaymentState;
  direction: string;
  amountNet: number;
  amountGross: number;
  currencyCode?: string | null;
  reference?: string | null;
  paymentDate?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  accountingDocument?: {
    id: string;
    number: string;
    tenantId?: string;
    type?: string;
    state?: string;
    amountGross?: number;
  } | null;
  paymentGateway?: { id: string; name: string } | null;
}

export interface Licence {
  id: string;
  tenantId: string;
  organizationUnitId?: string | null;
  databaseId?: string | null;
  subscriptionItemId?: string | null;
  serial: string;
  appName: string;
  appVersion: number;
  appType: string;
  appReglevel: number;
  state: string;
  type: string;
  registrationDate?: string | null;
  expirationDate?: string | null;
  deviceUid?: string | null;
  deviceBrand?: string | null;
  deviceModel?: string | null;
  deviceOS?: string | null;
  deviceInfo?: string | null;
  deviceDetails?: string | null;
  additionalPropertiesJson?: string | null;
  createdAt: string;
  updatedAt: string;
  tenant?: { id: string; name: string } | null;
  organizationUnit?: { id: string; name: string } | null;
  subscriptionItem?: { id: string } | null;
}

export interface PaymentAccount {
  id: string;
  tenantId: string;
  paymentGatewayId?: string | null;
  paymentTermId?: string | null;
  paymentGateway?: { id: string; name: string } | null;
  paymentTerm?: { id: string; name: string } | null;
  createdAt: string;
}

export interface PaymentGateway {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
}

export interface PaginatedResult<T> {
  nodes: T[];
  pageInfo?: { hasNextPage: boolean; endCursor?: string };
  totalCount?: number;
}
